/**
 * Per-ISIN scheme rules, read from the authoritative `get_schemes_by_isin`
 * response the admin panel already fetches through
 * `Api/mfAdminInvestments.fetchMFSchemeDetails`.
 *
 * Transcribed from the user platform's components/MutualFundDetails/utils.ts
 * and helpers/mfOperationEligibility.ts, retyped against the admin project's
 * `MFSchemeDetails`. The arithmetic is unchanged: the same amount band, the
 * same "every amount ends in zero" product rule folded into the step, the same
 * fail-closed reading of the operation flags.
 *
 * Eligibility is deliberately fail-closed. Only an explicit `true` from the
 * per-ISIN response permits an order; false, null, a missing field, a failed
 * request or a request still in flight must not submit one.
 */

import type { MFSchemeDetails } from "../../Api/mfAdminInvestments";
import { getAllowedMFInstallmentDays } from "../mfSchedule";

export type InvestmentType = "sip" | "lumpsum";

/** Product rule: every investment amount must end in a zero. */
export const STRICT_INVESTMENT_AMOUNT_MULTIPLE = 10;

export interface FundOperationAvailability {
  isAllowed: boolean;
  /** Sentence for a dialog or helper text. Null when allowed. */
  reason: string | null;
  /** Two-or-three word label for a disabled control. Null when allowed. */
  shortReason: string | null;
}

export interface InvestmentAmountRange {
  minAmount: number | null;
  maxAmount: number | null;
  thresholdMinAmount: number | null;
  thresholdMaxAmount: number | null;
  amountMultiple: number;
  amountStep: number;
  installmentsMin: number | null;
  dates: number[];
  frequency: string | null;
  isAvailable: boolean;
}

const OPERATION_ALLOWED: FundOperationAvailability = {
  isAllowed: true,
  reason: null,
  shortReason: null,
};

const blockOperation = (
  reason: string,
  shortReason: string
): FundOperationAvailability => ({ isAllowed: false, reason, shortReason });

export const getFundOperationAvailability = (
  details: MFSchemeDetails | undefined,
  operation: InvestmentType
): FundOperationAvailability => {
  const data = details?.data;
  if (!data) {
    return blockOperation(
      "This scheme's transaction eligibility could not be verified. Retry once the scheme details refresh.",
      "Checking availability"
    );
  }
  if (data.merged === true) {
    return blockOperation(
      "This scheme has merged into another and no longer accepts investments.",
      "Scheme merged"
    );
  }
  if (data.active === false) {
    return blockOperation(
      "This scheme is no longer active and does not accept investments.",
      "Scheme closed"
    );
  }
  if (operation === "lumpsum" && data.purchase_allowed !== true) {
    return blockOperation(
      "This scheme is not accepting one-time purchases at the moment.",
      "Buy unavailable"
    );
  }
  if (operation === "sip" && data.sip_allowed !== true) {
    return blockOperation(
      "This scheme does not accept SIP investments.",
      "SIP unavailable"
    );
  }
  return OPERATION_ALLOWED;
};

const getDecimalPlaces = (value: number) => {
  const valueText = String(value).toLowerCase();
  if (valueText.indexOf("e-") >= 0) {
    const parts = valueText.split("e-");
    const exponent = Number(parts[1]);
    const coefficientDecimals = (parts[0].split(".")[1] || "").length;
    return exponent + coefficientDecimals;
  }
  return (valueText.split(".")[1] || "").length;
};

const greatestCommonDivisor = (left: number, right: number): number => {
  let a = Math.abs(left);
  let b = Math.abs(right);
  while (b) {
    const remainder = a % b;
    a = b;
    b = remainder;
  }
  return a || 1;
};

/**
 * Combines the scheme's `amount_multiples` rule with the product rule that
 * every investment amount must end in zero.
 */
const getCommonAmountMultiple = (left: number, right: number) => {
  const decimalPlaces = Math.min(
    Math.max(getDecimalPlaces(left), getDecimalPlaces(right)),
    6
  );
  const scale = Math.pow(10, decimalPlaces);
  const scaledLeft = Math.round(left * scale);
  const scaledRight = Math.round(right * scale);
  const commonMultiple =
    Math.abs(scaledLeft * scaledRight) /
    greatestCommonDivisor(scaledLeft, scaledRight);
  return commonMultiple / scale;
};

const roundAmount = (value: number) =>
  Number(value.toFixed(Math.min(Math.max(getDecimalPlaces(value), 0), 6)));

const alignAmountUp = (value: number, multiple: number) =>
  roundAmount(Math.ceil((value - Number.EPSILON) / multiple) * multiple);

const alignAmountDown = (value: number, multiple: number) =>
  roundAmount(Math.floor((value + Number.EPSILON) / multiple) * multiple);

export const getInvestmentThreshold = (
  details: MFSchemeDetails | undefined,
  investmentType: InvestmentType,
  sipFrequency?: string
) => {
  const thresholds = details?.scheme_plans_v2?.thresholds || [];
  if (investmentType === "lumpsum") {
    return thresholds.find(
      (threshold) => String(threshold.type || "").trim().toLowerCase() === "lumpsum"
    );
  }
  const sipThresholds = thresholds.filter(
    (threshold) => String(threshold.type || "").trim().toLowerCase() === "sip"
  );
  const requested = sipFrequency ? sipFrequency.trim().toLowerCase() : "";
  if (requested) {
    return sipThresholds.find(
      (threshold) =>
        String(threshold.frequency || "").trim().toLowerCase() === requested
    );
  }
  return (
    sipThresholds.find(
      (threshold) =>
        String(threshold.frequency || "").trim().toLowerCase() === "monthly"
    ) || sipThresholds[0]
  );
};

export const getInvestmentAmountRange = (
  details: MFSchemeDetails | undefined,
  investmentType: InvestmentType,
  sipFrequency?: string
): InvestmentAmountRange => {
  const threshold = getInvestmentThreshold(details, investmentType, sipFrequency);
  const rawMin = Number(threshold?.amount_min);
  const thresholdMinAmount = Number.isFinite(rawMin) ? rawMin : null;
  const rawMax = Number(threshold?.amount_max);
  const thresholdMaxAmount =
    Number.isFinite(rawMax) &&
    (thresholdMinAmount == null || rawMax >= thresholdMinAmount)
      ? rawMax
      : null;
  const rawMultiple = Number(threshold?.amount_multiples);
  const amountMultiple =
    Number.isFinite(rawMultiple) && rawMultiple > 0 ? rawMultiple : 1;
  const amountStep = getCommonAmountMultiple(
    amountMultiple,
    STRICT_INVESTMENT_AMOUNT_MULTIPLE
  );
  const minAmount =
    thresholdMinAmount == null
      ? null
      : alignAmountUp(thresholdMinAmount, amountStep);
  const maxAmount =
    thresholdMaxAmount == null
      ? null
      : alignAmountDown(thresholdMaxAmount, amountStep);
  const isAvailable =
    minAmount != null && maxAmount != null && maxAmount >= minAmount;
  const rawInstallmentsMin = Number(threshold?.installments_min);

  return {
    minAmount: isAvailable ? minAmount : null,
    maxAmount: isAvailable ? maxAmount : null,
    thresholdMinAmount,
    thresholdMaxAmount,
    amountMultiple,
    amountStep,
    installmentsMin: Number.isFinite(rawInstallmentsMin)
      ? Math.max(1, Math.ceil(rawInstallmentsMin))
      : null,
    // Intersected with what the frequency permits — weekday 1-5 for a weekly
    // or fortnightly plan, day-of-month 1-28 otherwise. See Helpers/mfSchedule.
    dates: getAllowedMFInstallmentDays(
      (sipFrequency || threshold?.frequency || "").trim().toLowerCase(),
      (threshold?.dates || []).filter((date) => Number.isInteger(date))
    ),
    frequency: threshold?.frequency
      ? String(threshold.frequency).trim().toLowerCase()
      : null,
    isAvailable,
  };
};

export const getSipInvestmentAmountRanges = (
  details: MFSchemeDetails | undefined
): InvestmentAmountRange[] => {
  const frequencies: string[] = [];
  (details?.scheme_plans_v2?.thresholds || []).forEach((threshold) => {
    if (String(threshold.type || "").trim().toLowerCase() !== "sip") return;
    const frequency = String(threshold.frequency || "").trim().toLowerCase();
    if (frequency && frequencies.indexOf(frequency) < 0) frequencies.push(frequency);
  });

  return frequencies
    .map((frequency) => getInvestmentAmountRange(details, "sip", frequency))
    .filter((range) => range.frequency != null)
    .sort((left, right) => {
      if (left.frequency === "monthly") return -1;
      if (right.frequency === "monthly") return 1;
      return 0;
    });
};

/** Lock-in is returned in months even though the AMC quotes years. */
export const getSchemeLockIn = (
  details: MFSchemeDetails | undefined
): { lockIn: boolean; lockInPeriod: number | null } => {
  const lockIn = details?.data?.lock_in === true;
  const raw = Number(details?.data?.lock_in_period);
  return {
    lockIn,
    lockInPeriod: Number.isFinite(raw) && raw > 0 ? raw : null,
  };
};
